'use client'

import React, { useEffect, useRef, useState } from 'react'
import { ChatHeader } from './components/ChatHeader'
import { ChatMessage } from './components/ChatMessage'
import { ChatInput, tools } from './components/ChatInput'
import { TypingIndicator } from './components/TypingIndicator'
import { useChatbot } from './hooks/useChatbox'
import type { ChatbotProps } from './types'
import { injectChatbotStyles } from './utils/styles'
import ChatLoadingAnimation from '@/app/builder/_components/ChatLoadingAnimation'
import StreamingProgressBar from '@/app/builder/_components/StreamingProgressBar'
import SectionPromptLoader from './components/SectionPromptLoader'
import ToolOverlay from '@/app/builderV2/_components/ToolOverlay'
import GrapesJSBlocksPanel from '@/app/builderV2/_components/panels/GrapesJSBlocksPanel'
import GrapesJSLayersPanel from '@/app/builderV2/_components/panels/GrapesJSLayersPanel'
import GrapesJSStylePanel from '@/app/builderV2/_components/panels/GrapesJSStylePanel'
import GrapesJSTraitPanel from '@/app/builderV2/_components/panels/GrapesJSTraitPanel'
import { resumeGeneration } from './utils/api'

const Chatbot: React.FC<ChatbotProps> = ({
  generatedResponse = {},
  setContent = () => {},
  setBlocksAnimationLoading,
  config = {
    title: 'Walter AI',
    initialOpen: true,
    showNewMessage: true,
    showHistory: true
  },
  pageDetails,
  flowHandler,
  initialData = {},
  setIsOpenState: setIsOpenProp,
  setStreamingProgress,
  setIsStreaming,
  setIsGeneratingSectionPrompts,
  getEditorInstance
}) => {
  const [isOpen, setIsOpen] = useState(config.initialOpen)
  const [isMobile, setIsMobile] = useState(false)
  const [isIPadMini, setIsIPadMini] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [keyboardVisible, setKeyboardVisible] = useState(false)
  const [currentPage, setCurrentPage] = useState<string>('home')
  const [windowWidth, setWindowWidth] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [isEditModeEnabled, setIsEditModeEnabled] = useState(false)
  const isDefaultFlow = !flowHandler
  const [isLaptop, setIsLaptop] = useState(false)
  // Tool overlay state
  const [activeToolOverlay, setActiveToolOverlay] = useState<string | null>(
    null
  )
  const [isOverlayExpanded, setIsOverlayExpanded] = useState(false)
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false)

  const chatbotData = {
    ...generatedResponse,
    ...initialData,
    pageDetails
  }

  // Enhanced content handler for multi-page support
  const handleSetContent = (content: string, pageName: string = 'home') => {
    setContent(content, pageName)
    setCurrentPage(pageName)
    if (content && content.trim() !== '') {
      setHasGeneratedContent(true)
    }
  }

  const {
    isTyping,
    messages,
    handleUserInput,
    handleChoiceSelect,
    heroVariants,
    handleVariantSelect,
    selectedVariantId,
    isGeneratingContent,
    isGeneratingPage,
    isGeneratingSectionPrompts: isGeneratingSectionPromptsInternal,
    availablePages,
    currentState,
    resetChat,
    isLoadingMessages,
    handleRepoIgnore,
    handleRepoSelect,
    selectedRepoId
  } = useChatbot(
    {
      ...chatbotData,
      isEditModeEnabled // Pass the edit mode state
    },
    handleSetContent,
    setIsOpen,
    flowHandler,
    setBlocksAnimationLoading,
    setStreamingProgress,
    setIsStreaming,
    setIsGeneratingSectionPrompts
  )

  console.log({currentState})

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSetIsOpen = (value: boolean) => {
    setIsOpen(value)
    setIsOpenProp?.(value)
  }

  const handleToolClick = (toolTitle: string) => {
    // If clicking the same tool that's already active, close it
    if (activeToolOverlay === toolTitle) {
      setActiveToolOverlay(null)
      return
    }

    // Open new overlay with the same expanded state as the previous one
    setActiveToolOverlay(toolTitle)
    // No need to change isOverlayExpanded state as it will persist
  }

  const handleCloseOverlay = () => {
    setActiveToolOverlay(null)
  }

  const handleToggleExpand = () => {
    setIsOverlayExpanded(!isOverlayExpanded)
  }

  // Render appropriate content based on the active tool
  const renderToolOverlayContent = () => {
    const editor = getEditorInstance ? getEditorInstance() : null

    const handleStyleAutoFocus = () => {
      if (activeToolOverlay !== 'Open Style Manager') {
        setActiveToolOverlay('Open Style Manager')
      }
    }

    switch (activeToolOverlay) {
      case 'Open Blocks':
        return <GrapesJSBlocksPanel editor={editor} />
      case 'Open Layer Manager':
        return <GrapesJSLayersPanel editor={editor} />
      case 'Settings':
        return <GrapesJSTraitPanel editor={editor} />
      case 'Open Style Manager':
        return (
          <GrapesJSStylePanel
            editor={editor}
            onAutoFocus={handleStyleAutoFocus}
          />
        )
      case 'AI Content':
        return (
          <div className='flex h-full items-center justify-center text-gray-400'>
            AI Content generation panel will be implemented soon
          </div>
        )
      default:
        return (
          <div className='flex h-full items-center justify-center text-gray-400'>
            {activeToolOverlay} panel content will be implemented soon
          </div>
        )
    }
  }

  // Detect iPad Mini
  const detectIPadMini = () => {
    if (typeof window === 'undefined') return false

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // iPad Mini 2021 viewport dimensions are around 744 x 1059
    const isIPadMiniPortrait =
      viewportWidth >= 740 &&
      viewportWidth <= 750 &&
      viewportHeight >= 1050 &&
      viewportHeight <= 1070

    const isIPadMiniLandscape =
      viewportWidth >= 1050 &&
      viewportWidth <= 1070 &&
      viewportHeight >= 740 &&
      viewportHeight <= 750

    return isIPadMiniPortrait || isIPadMiniLandscape
  }

  // Update the device detection logic
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth
      setWindowWidth(width)
      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width <= 1024)
      setIsLaptop(width >= 1024)
      setIsIPadMini(detectIPadMini())
    }

    checkDeviceType()
    window.addEventListener('resize', checkDeviceType)
    return () => window.removeEventListener('resize', checkDeviceType)
  }, [])

  // Add this useEffect right after the other useEffect hooks in the Chatbot component
  useEffect(() => {
    // Check if we have necessary data to attempt resume
    if (pageDetails?.pageId && hasGeneratedContent === false) {
      const attemptResumeGeneration = async () => {
        try {
          const currentState = chatbotData.currentState || {}

          // Call resume generation API
          const result = await resumeGeneration(
            {
              prompts: currentState.previousSections || [],
              selected_hero: currentState.selectedHero || '',
              page_name: currentState.currentPage || 'home',
              website_type: currentState.websiteType || 'single',
              page_id: pageDetails.pageId
            },
            {
              onProgress: percentage => {
                // Only start showing progress once we get the first update
                if (percentage > 0) {
                  setIsStreaming?.(true)
                  setStreamingProgress?.(percentage)
                }
              },
              onComplete: () => {
                setTimeout(() => {
                  setIsStreaming?.(false)
                  setStreamingProgress?.(100)
                }, 500)
              }
            }
          )
          console.log(result)
          // If content was resumed successfully, update UI
          if (result && result.status === 'completed' && result.content) {
            console.log('Setting complete content from resume generation')

            // Always use the complete content returned from the API
            handleSetContent(result.content, currentState.currentPage || 'home')
          } else if (
            result &&
            (result.status === 'completed' || result.status === 'invalid')
          ) {
            // No need to show loading indicators for completed or invalid status
            setIsStreaming?.(false)
          }
        } catch (error) {
          console.error('Error resuming generation:', error)
          setIsStreaming?.(false)
        }
      }

      attemptResumeGeneration()
    }
  }, [pageDetails?.pageId])

  useEffect(() => {
    if (
      isMobile &&
      hasGeneratedContent &&
      !isGeneratingContent &&
      !isGeneratingPage
    ) {
      // Close the chatbot after a small delay to ensure UI updates are complete
      const timer = setTimeout(() => {
        handleSetIsOpen(false)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [isMobile, hasGeneratedContent, isGeneratingContent, isGeneratingPage])

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const isKeyboardVisible =
          window.visualViewport.height < window.innerHeight * 0.7
        setKeyboardVisible(isKeyboardVisible)

        if (chatContainerRef.current && isKeyboardVisible) {
          chatContainerRef.current.style.height = `${window.visualViewport.height}px`
          // Add smooth adjustment when keyboard appears
          chatContainerRef.current.style.transition = 'height 0.3s ease'
          // Find the input element and scroll it into view
          const inputElement = chatContainerRef.current.querySelector(
            '.chatbot-input-container'
          )
          if (inputElement) {
            // Add a slight delay to ensure the view is adjusted after keyboard is fully visible
            setTimeout(() => {
              inputElement.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }, 100)
          } else {
            // If input element not found, at least scroll to bottom
            scrollToBottom()
          }
        } else if (chatContainerRef.current) {
          // Reset height when keyboard is hidden
          chatContainerRef.current.style.height = ''
        }
      }
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize)
      return () =>
        window.visualViewport?.removeEventListener('resize', handleResize)
    }

    // Initial detection
    setIsIPadMini(detectIPadMini())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Inject styles
  useEffect(() => {
    injectChatbotStyles()
  }, [])

  useEffect(() => {
    setIsOpen(config.initialOpen)
  }, [config.initialOpen])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  useEffect(() => {
    if (isOpen) {
      scrollToBottom()
    }
  }, [isOpen])

  // Add effect to reset chat when edit mode changes
  useEffect(() => {
    if (resetChat) {
      resetChat()
    }
  }, [isEditModeEnabled])

  // Generate suggestions for available pages
  const getPageSuggestions = () => {
    if (!availablePages || availablePages.length === 0) return null

    return (
      <div className='mb-2 mt-4'>
        <p className='mb-2 text-sm text-gray-500'>
          Available pages to generate:
        </p>
        <div className='flex flex-wrap gap-2'>
          {availablePages.map((page, index) => (
            <button
              key={index}
              className='rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800 transition-colors hover:bg-blue-200'
              onClick={async () => {
                try {
                  handleUserInput(`Generate ${page} page`)
                  setCurrentPage(page)
                } catch (error) {
                  console.error(
                    `Error handling page generation for ${page}:`,
                    error
                  )
                }
              }}
            >
              {page.charAt(0).toUpperCase() + page.slice(1)}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // if (isLoadingMessages) {
  //   return (
  //     <div className="flex items-center justify-center h-full">
  //       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  //     </div>
  //   );
  // }

  const chatboxContainerClass = `
    flex flex-col 
    ${isMobile ? 'h-[100svh]' : isLaptop ? 'h-full' : windowWidth >= 768 && windowWidth <= 820 ? 'h-[104%]' : 'h-[106%]'}
    w-full ${isDefaultFlow ? 'bg-[#1E1E1E]' : 'bg-white'} md:rounded-lg md:shadow-lg overflow-hidden
    ${isIPadMini ? 'ipad-mini-container' : ''}
  `.trim()

  return (
    <div
      ref={chatContainerRef}
      className={`fixed z-50 ${
        isOpen
          ? `chatbot-responsive-width ${isDefaultFlow ? 'left-0' : 'right-0'} top-0 lg:top-[40px]`
          : `bottom-4 ${isDefaultFlow ? 'left-4' : 'right-4'}`
      } ${isIPadMini ? 'ipad-mini-chatbot' : ''} `}
    >
      {isOpen ? (
        <div className={chatboxContainerClass}>
          {!isDefaultFlow && (
            <ChatHeader
              onClose={() => handleSetIsOpen(false)}
              className={`flex-none border-b ${isDefaultFlow ? 'border-gray-700 bg-[#1E1E1E] text-white' : ''}`}
              title={
                currentPage !== 'home'
                  ? `${config.title} - ${currentPage.charAt(0).toUpperCase() + currentPage.slice(1)} Page`
                  : config.title || 'Default Title'
              }
              showNewMessage={config.showNewMessage ?? true}
              showHistory={config.showHistory ?? true}
            />
          )}

          {isDefaultFlow && (
            <div className='mt-10 flex flex-none justify-end border-b border-gray-700 bg-[#1E1E1E] px-4 py-2 text-white lg:hidden'>
              <button
                onClick={() => handleSetIsOpen(false)}
                className='text-white transition-colors hover:text-gray-200'
              >
                <img
                  src='/images/close.svg'
                  alt='Close'
                  width={20}
                  height={20}
                />
              </button>
            </div>
          )}

          <div
            className={`flex-1 overflow-y-auto ${isDefaultFlow ? 'bg-[#1E1E1E] text-white' : 'bg-white'} relative ${isMobile ? 'max-h-[calc(100svh-125px)]' : 'max-h-[calc(100svh-95px)]'}`}
          >
            {/* Rest of the chat content */}
            {isLoadingMessages ? (
              <div className='flex h-full items-center justify-center'>
                <div
                  className={`h-8 w-8 animate-spin rounded-full border-b-2 ${isDefaultFlow ? 'border-blue-400' : 'border-primary'}`}
                />
              </div>
            ) : (isMobile || currentPage !== 'home') &&
              (isGeneratingSectionPromptsInternal ||
                isGeneratingContent ||
                isGeneratingPage) ? (
              <div className='flex h-full items-center justify-center p-4'>
                {isGeneratingSectionPromptsInternal ? (
                  <SectionPromptLoader isLoading={true} />
                ) : (
                  <StreamingProgressBar
                    progress={currentState.streamingProgress || 0}
                    isStreaming={true}
                    inChatMode={true}
                  />
                )}
              </div>
            ) : (
              <div
                className={`p-4 ${isDefaultFlow ? 'space-y-0' : 'space-y-4'} ${activeToolOverlay && !isOverlayExpanded ? 'pb-[50%]' : ''}`}
              >
                {messages.map((message, index) => (
                  <ChatMessage
                    key={index}
                    message={message}
                    heroVariants={message.showVariants ? heroVariants : null}
                    onVariantSelect={handleVariantSelect}
                    selectedVariantId={selectedVariantId}
                    onChoiceSelect={handleChoiceSelect}
                    isDefaultFlow={isDefaultFlow}
                    repos={message.repos}
                    onRepoSelect={handleRepoSelect}
                    onRepoIgnore={handleRepoIgnore}
                    selectedRepoId={selectedRepoId}
                    websiteType={currentState?.websiteType ?? ''}
                    isTyping={isTyping}
                    isRepoSelected={currentState?.isRepoSelected ?? ''}
                  />
                ))}
                {isTyping && <TypingIndicator />}

                {/* Show available pages when hero is selected and home page is generated */}
                {!isTyping &&
                  !isGeneratingContent &&
                  availablePages &&
                  availablePages.length > 0 &&
                  (!currentState.isEditMode ||
                    !currentState.currentEditRequest) &&
                  getPageSuggestions()}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Tool overlay */}
          {isDefaultFlow && (
            <div
              className={`absolute left-6 right-6 ${
                isOverlayExpanded
                  ? 'bottom-0 top-0 z-20'
                  : 'bottom-[170px] top-20 z-10'
              } transition-all duration-300 ${
                activeToolOverlay
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none translate-y-full opacity-0'
              }`}
            >
              <ToolOverlay
                title={activeToolOverlay || ''}
                onClose={handleCloseOverlay}
                onExpand={handleToggleExpand}
                isExpanded={isOverlayExpanded}
              >
                {activeToolOverlay && renderToolOverlayContent()}
              </ToolOverlay>
            </div>
          )}

          <ChatInput
            onSend={handleUserInput}
            className={`chatbot-input-container flex-none border-t ${isDefaultFlow ? 'border-gray-700 bg-[#1E1E1E] text-white' : 'bg-white'} ${isIPadMini ? 'ipad-mini-input' : ''}`}
            disabled={isGeneratingContent || isGeneratingPage}
            placeholder={
              isGeneratingContent
                ? 'Generating home page...'
                : isGeneratingPage
                  ? `Generating ${currentPage} page...`
                  : 'Type your message...'
            }
            isDefaultFlow={isDefaultFlow}
            onToolClick={handleToolClick}
            activeToolOverlay={activeToolOverlay}
          />
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-4 ${isDefaultFlow ? 'left-4' : 'right-4'} flex h-12 w-12 items-center justify-center rounded-full bg-[#FF6600] shadow-lg transition-colors hover:bg-blue-600`}
        >
          <img src='/images/AI.svg' alt='Chatbot' className='min-h-6 min-w-6' />
        </button>
      )}
    </div>
  )
}

export default Chatbot
